import { useMutation } from '@tanstack/react-query';
import { chakra, Input, Box, Text, Flex, Fieldset } from '@chakra-ui/react';
import { Controller, FormProvider, Resolver, SubmitHandler, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useNavigate, useRouteLoaderData } from 'react-router-dom';
import { api, LoginResponseDto, RoleDto, UserDto } from '../../lib/api';
import { CreateUserDto, CompanyDto, BranchDto } from '../../lib/api';
import Address from '../../components/address';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toaster } from '@/components/ui/toaster';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { addressOptionalValidationSchema } from '@musa/lib';

const branchValidationSchema = z.object({
    companyId: z.string().min(1, 'Required'),
    branchId: z.string().min(1, 'Required'),
    roleId: z.string().min(1, 'Required'),
});

const validationSchema = zodResolver(
    z
        .object({
            email: z.string().email('Must be a valid email'),
            lastName: z.string().min(1, 'Last name is required'),
            firstName: z.string().min(1, 'First name is required'),
            phone: z.string().nullish(),
            orgAdmin: z.boolean().optional(),
            active: z.boolean().optional(),
            discipline: z.string().min(1, 'Discipline is required'),
            address: addressOptionalValidationSchema,
            branches: z.array(branchValidationSchema).optional(),
        })
        .superRefine((data, ctx) => {
            if (!data.orgAdmin && (!data.branches || data.branches.length < 1)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['branches'],
                    message: 'At least one branch is required',
                });
            }
        }),
);

const defaultValues = {
    email: '',
    active: true,
    firstName: '',
    lastName: '',
    phone: '',
    branches: [],
    fax: '',
    orgAdmin: false,
    address: {
        address: '',
        city: '',
        state: '',
        zipcode: '',
    },
};

export function UserPage() {
    const navigate = useNavigate();
    const session = useRouteLoaderData('root') as LoginResponseDto;
    const { companies, roles, user } = useRouteLoaderData('user-details') as {
        companies: CompanyDto[];
        roles: RoleDto[];
        user?: UserDto;
    };

    const { mutate, isPending } = useMutation<unknown, unknown, CreateUserDto>({
        mutationFn: async (data) => {
            if (!user) {
                return api.user.create(data);
            }
            return api.user.update(user.id, data);
        },
        onSuccess: () => {
            toaster.create({
                title: user ? 'User updated' : 'User created',
                type: 'success',
                duration: 3000,
            });
            navigate('/organization/user-management');
        },
    });

    const rhf = useForm<CreateUserDto>({
        defaultValues: { ...defaultValues, ...user },
        resolver: validationSchema as unknown as Resolver<CreateUserDto>,
        mode: 'onChange',
        reValidateMode: 'onChange',
    });
    const {
        register,
        formState: { errors },
    } = rhf;

    const onSubmit: SubmitHandler<CreateUserDto> = (data) => {
        mutate(data);
    };

    return (
        <Flex gap="8" flexDirection="column" maxW="600px">
            <FormProvider {...rhf}>
                <chakra.form noValidate onSubmit={rhf.handleSubmit(onSubmit)} w="100%">
                    <Flex flexDirection="column" gap="24px">
                        <Card
                            type="standard"
                            barType={
                                !!errors.email || !!errors.firstName || !!errors.lastName || !!errors.discipline ? 'error' : 'standard'
                            }
                            barTitle="User Details"
                            w="100%">
                            <FieldGroup>
                                <Field label="E-mail" required invalid={!!errors?.email} errorText={errors?.email?.message}>
                                    <Input type="text" {...register('email')} variant="outline" />
                                </Field>
                                <FieldRow>
                                    <Field label="First Name" required invalid={!!errors?.firstName} errorText={errors?.firstName?.message}>
                                        <Input type="text" {...register('firstName')} />
                                    </Field>
                                    <Field label="Last Name" required invalid={!!errors?.lastName} errorText={errors?.lastName?.message}>
                                        <Input type="text" {...register('lastName')} />
                                    </Field>
                                </FieldRow>

                                <FieldRow>
                                    <Field
                                        label="Discipline"
                                        required
                                        invalid={!!errors?.discipline}
                                        errorText={errors?.discipline?.message}>
                                        <NativeSelectRoot>
                                            <NativeSelectField w="100%" {...register('discipline')}>
                                                {[
                                                    '',
                                                    { discipline: 'Skilled Nursing', titles: ['RN', 'LPN', 'LVN'] },
                                                    { discipline: 'Home Aide', titles: ['CNA', 'HHA', 'CHHA'] },
                                                    { discipline: 'Physical Therapy', titles: ['PT', 'PTA'] },
                                                    { discipline: 'Occupational Therapy', titles: ['OT', 'OTA'] },
                                                    { discipline: 'Speech Therapy', titles: ['ST'] },
                                                    { discipline: 'Social Work', titles: ['MSW'] },
                                                ].map((element: string | { discipline: string; titles: string[] }) => {
                                                    if (typeof element === 'string') {
                                                        return <option key={0}></option>;
                                                    }
                                                    return (
                                                        <optgroup key={element.discipline} label={element.discipline}>
                                                            {element.titles.map((title) => (
                                                                <option key={title}>{title}</option>
                                                            ))}
                                                        </optgroup>
                                                    );
                                                })}
                                            </NativeSelectField>
                                        </NativeSelectRoot>
                                    </Field>
                                    <Flex gap="12px">
                                        <Field
                                            label="Admin"
                                            orientation="vertical"
                                            invalid={!!rhf.formState.errors.orgAdmin}
                                            errorText={rhf.formState.errors.orgAdmin?.message}>
                                            <Flex h="100%">
                                                <Controller
                                                    name="orgAdmin"
                                                    control={rhf.control}
                                                    render={({ field }) => (
                                                        <Switch
                                                            name={field.name}
                                                            checked={field.value}
                                                            disabled={session.id === user?.id}
                                                            onCheckedChange={({ checked }) => field.onChange(checked)}
                                                            inputProps={{ onBlur: field.onBlur }}
                                                        />
                                                    )}
                                                />
                                            </Flex>
                                        </Field>
                                        <Field label="Active" orientation="vertical">
                                            <Flex h="100%">
                                                <Controller
                                                    name="active"
                                                    control={rhf.control}
                                                    render={({ field }) => (
                                                        <Switch
                                                            name={field.name}
                                                            checked={field.value}
                                                            disabled={session.id === user?.id}
                                                            onCheckedChange={({ checked }) => field.onChange(checked)}
                                                            inputProps={{ onBlur: field.onBlur }}
                                                        />
                                                    )}
                                                />
                                            </Flex>
                                        </Field>
                                    </Flex>
                                </FieldRow>
                            </FieldGroup>
                        </Card>
                        <Card type="standard" barType={errors.branches ? 'error' : 'standard'} barTitle="User Roles" w="100%">
                            <Roles roles={roles} companies={companies} />
                        </Card>
                        <Card type="standard" barType={errors.address ? 'error' : 'standard'} barTitle="Contact Details" w="100%">
                            <Contact />
                        </Card>
                        <Flex>
                            <Button
                                type="submit"
                                disabled={rhf.formState.isSubmitting}
                                loadingText="saving"
                                loading={isPending}
                                data-testid="button-submit">
                                Save
                            </Button>
                        </Flex>
                    </Flex>
                </chakra.form>
            </FormProvider>
        </Flex>
    );
}

interface RolesProps {
    roles: RoleDto[];
    companies: CompanyDto[];
}

const Roles = ({ roles, companies }: RolesProps) => {
    const {
        getValues,
        control,
        formState: { errors },
    } = useFormContext<CreateUserDto>();

    const { append, remove, update } = useFieldArray({
        control,
        name: 'branches',
    });

    useWatch({
        control,
        name: ['branches', 'orgAdmin'],
    });

    return (
        <Fieldset.Root invalid={!!errors?.branches && !getValues('orgAdmin')}>
            <Fieldset.Content>
                {companies?.map((company: CompanyDto) => {
                    return (
                        <Flex key={company.id} flexDirection="column" gap="8px">
                            <Text fontWeight={500} fontSize="md">
                                {company.name}
                            </Text>
                            {company.branches?.map((branch: BranchDto) => {
                                const selectedBranches = getValues('branches') || [];
                                const selectedBranchIndex = selectedBranches?.findIndex(({ branchId }) => branchId === branch.id);
                                const selectedBranch = selectedBranches[selectedBranchIndex];
                                return (
                                    <Field
                                        pl="16px"
                                        css={{ '--field-label-width': '150px' }}
                                        key={branch.id}
                                        label={branch.name}
                                        orientation="horizontal">
                                        <NativeSelectRoot>
                                            <NativeSelectField
                                                value={selectedBranch?.roleId}
                                                onChange={(e) => {
                                                    const target = e.target as unknown as HTMLSelectElement;
                                                    if (!target.value) {
                                                        remove(selectedBranchIndex);
                                                    } else {
                                                        const role = {
                                                            companyId: company.id,
                                                            branchId: branch.id,
                                                            roleId: target.value,
                                                        };
                                                        if (selectedBranchIndex !== -1) {
                                                            update(selectedBranchIndex, role);
                                                        } else {
                                                            append(role);
                                                        }
                                                    }
                                                }}>
                                                <option key="empty" value="" />
                                                {roles?.map((role) => {
                                                    return (
                                                        <option key={role.id} value={role.id}>
                                                            {role.name}
                                                        </option>
                                                    );
                                                })}
                                            </NativeSelectField>
                                        </NativeSelectRoot>
                                    </Field>
                                );
                            })}
                        </Flex>
                    );
                })}
            </Fieldset.Content>
            <Fieldset.ErrorText>{errors?.branches?.message}</Fieldset.ErrorText>
        </Fieldset.Root>
    );
};

const Contact = () => {
    const {
        control,
        register,
        formState: { errors },
    } = useFormContext<CreateUserDto>();
    const address = useWatch({ control, name: 'address' });

    const hasAddressInput = Object.values(address).some(Boolean);

    return (
        <Flex flexDirection="column" gap="16px" w="100%">
            <Flex gap="14px" marginTop="4" wrap={{ base: 'wrap', md: 'nowrap' }}>
                <Box w="100%">
                    <Field label="Phone" invalid={!!errors.phone} errorText={errors.phone?.message}>
                        <Input type="text" {...register('phone')} pattern="\d*" maxLength={10} />
                    </Field>
                </Box>
                <Box w="100%">
                    <Field label="Fax" errorText={errors.fax?.message}>
                        <Input type="text" {...register('fax')} pattern="\d*" maxLength={10} />
                    </Field>
                </Box>
            </Flex>
            <Address
                isRequired={hasAddressInput}
                validAddress={!!errors.address?.address}
                validCity={!!errors.address?.city}
                validState={!!errors.address?.state}
                validZipCode={!!errors.address?.zipcode}
                errorAddress={errors.address?.address?.message}
                errorCity={errors.address?.city?.message}
                errorState={errors.address?.state?.message}
                errorZipCode={errors.address?.zipcode?.message}
                nameAddress="address.address"
                nameCity="address.city"
                nameState="address.state"
                nameZipCode="address.zipcode"
            />
        </Flex>
    );
};
